import Logger from '#helpers/logging/Logger.js'
import redirectToLogin from '#helpers/redirectToLogin.js'
import actionsRoutes from '#modules/actions/routes.actions.js'
import adminRoutes from '#modules/admin/routes.admin.js'
import auditsRoutes from '#modules/audits/routes.audits.js'
import authRoutes from '#modules/auth/routes.auth.js'
import companyRoutes from '#modules/company/routes.company.js'
import homeRoutes from '#modules/home/routes.home.js'
import innovativeFreshRoutes from '#modules/innovative-fresh/routes.innovative-fresh.js'
import manualsRoutes from '#modules/manuals/routes.manuals.js'
import registrationRoutes from '#modules/registrations/routes.registrations.js'
import sensoryRoutes from '#modules/sensory/routes.sensory.js'
import settingsRoutes from '#modules/settings/routes.settings.js'
import suppliersIndexRoutes from '#modules/suppliers-index/routes.supplier-index.js'
import suppliersRoutes from '#modules/suppliers/routes.suppliers.js'
import userRoutes from '#modules/user/routes.user.js'
import baseRoutes from '#src/router/routes.base.js'
import { useAuthorizationStore } from '#stores/useAuthStore.js'
import { useFeaturesStore } from '#stores/useFeaturesStore.js'
import { usePermissionsStore } from '#stores/usePermissionsStore.js'
import { useSettingsStore } from '#stores/useSettingsStore.js'
import { createRouter, createWebHistory } from 'vue-router'

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		...baseRoutes,
		...authRoutes,
		homeRoutes,
		companyRoutes,
		suppliersRoutes,
		settingsRoutes,
		manualsRoutes,
		registrationRoutes,
		sensoryRoutes,
		adminRoutes,
		auditsRoutes,
		userRoutes,
		actionsRoutes,
		suppliersIndexRoutes,
		innovativeFreshRoutes,
	],
})

router.beforeEach(async (to, from, next) => {
	const auth = useAuthorizationStore()
	Logger.debug(`router.beforeEach: check route ${to.path}`)

	// mark the start of calculations
	performance.mark('routing-start')

	if (to.matched.some(record => record.meta.requiresAuth)) {
		Logger.debug('router.beforeEach: requiresAuth')
		// There is no active session
		if (!auth.isSessionActive()) {
			Logger.debug('no active session')
			await auth.logout() // Logout the user and remove all session info
			const url = to.path // Get URL
			performance.mark('routing-end')
			performance.measure('routing', 'routing-start', 'routing-end')
			return redirectToLogin(next, url)
		}

		// A token was found. Check if we can still refresh it
		const now = Date.now() / 1000 // get seconds instead of ms
		const sessionExpires = auth.expires // when the token expires
		const refreshFrom = sessionExpires - auth.refreshTime // time from when it expires

		// Session is *almost* over.
		if (now < sessionExpires && now > refreshFrom) {
			Logger.debug('router.beforeEach: Session is about to expire. trying to refresh')
			// Try to refresh
			await auth.refresh()
		}
	}

	if (to.matched.some(record => record.meta.requiresLogout)) {
		Logger.debug('router.beforeEach: requiresLogout')
		if (auth.isSessionActive()) {
			performance.mark('routing-end')
			performance.measure('routing', 'routing-start', 'routing-end')
			return next({ path: '/' }) // redirect to home page
		}
	}

	if (to.matched.some(record => record.meta.permissions)) {
		Logger.debug('router.beforeEach: permissions')
		if (!auth.isSessionActive()) {
			await auth.logout() // Logout the user and remove all session info
			const url = to.path // Get URL
			performance.mark('routing-end')
			performance.measure('routing', 'routing-start', 'routing-end')
			return redirectToLogin(next, url)
		}

		const permissions = usePermissionsStore()
		await permissions.fetch()
		if (!permissions.has(...to.meta.permissions)) {
			Logger.debug('router.beforeEach: missing permissions', to.meta.permissions)
			performance.mark('routing-end')
			performance.measure('routing', 'routing-start', 'routing-end')
			return next({ name: '403' })
		}
	}

	if (to.matched.some(record => record.meta.permissionsAny)) {
		Logger.debug('router.beforeEach: permissions')
		if (!auth.isSessionActive()) {
			await auth.logout() // Logout the user and remove all session info
			const url = to.path // Get URL
			performance.mark('routing-end')
			performance.measure('routing', 'routing-start', 'routing-end')
			return redirectToLogin(next, url)
		}

		const permissions = usePermissionsStore()
		await permissions.fetch()

		if (!permissions.hasAny(...to.meta.permissionsAny)) {
			Logger.debug('router.beforeEach: missing permissions', to.meta.permissionsAny)
			performance.mark('routing-end')
			performance.measure('routing', 'routing-start', 'routing-end')
			return next({ name: '403' })
		}
	}
	if (to.matched.some(record => record.meta.setting)) {
		const settings = useSettingsStore()
		await settings.fetch()
		const record = to.matched.find(r => r.meta.setting)
		if (settings.get(record.meta.setting.module, record.meta.setting.key) !== '1') {
			Logger.debug('router.beforeEach: setting disabled', record.meta.setting)
			return next({ name: '403' })
		}
	}

	if (to.matched.some(record => record.meta.settingsAny)) {
		const settings = useSettingsStore()
		await settings.fetch()
		const record = to.matched.find(r => r.meta.settingsAny)
		const anyEnabled = record.meta.settingsAny.some(
			setting => settings.get(setting.module, setting.key) === '1',
		)
		if (!anyEnabled) {
			Logger.debug('router.beforeEach: settings disabled', record.meta.settingsAny)
			return next({ name: '403' })
		}
	}

	if (to.matched.some(record => record.meta.featureDisabled)) {
		const features = useFeaturesStore()
		await features.fetch()
		const record = to.matched.find(r => r.meta.featureDisabled)
		if (features.isActive(record.meta.featureDisabled)) {
			Logger.debug('router.beforeEach: feature active, route retired', record.meta.featureDisabled)
			return next({ name: '403' })
		}
	}

	if (!to.matched.length) {
		Logger.debug('route notFound')
		performance.mark('routing-end')
		performance.measure('routing', 'routing-start', 'routing-end')
		return next({ name: '404' })
	}

	performance.mark('routing-end')
	performance.measure('routing', 'routing-start', 'routing-end')
	return next()
})

export default router
